
  import {computed, defineComponent, onMounted, reactive, Ref, toRefs} from "vue";
  import InventoriesCompanyLocationsFormCard from "./InventoriesCompanyLocationsFormCard.vue";
  import InventoriesCompanyLocationsGeneralForm from "../forms/InventoriesCompanyLocationsGeneralForm.vue";
  import InventoriesCompanyLocationsAddressForm from "../forms/InventoriesCompanyLocationsAddressForm.vue";
  import ModalDialog from "@/components/ModalDialog.vue";
  import {MessageTypes} from "@/components/message-types";
  import CompanyLocationService, {CompanyLocationV2Dto, CompanyLocationV2Request} from "@/service/CompanyLocationService";
  import {AxiosError} from "axios";
  import eventBus from "@/eventbus";
  import {helpers, required} from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import {t} from "@/mixin/mixins";

  export default defineComponent({
    name: "InventoriesCompanyLocationsDialog",
    components: {
      InventoriesCompanyLocationsFormCard,
      InventoriesCompanyLocationsGeneralForm,
      InventoriesCompanyLocationsAddressForm,
      ModalDialog
    },
    setup() {

      const state = reactive({
        dialog: false,
        edit: false,
        companyLocation: {} as CompanyLocationV2Dto,
        oldCompanyLocationName: "",
        serverErrors: new Map<string, string>()
      });

      onMounted(() => {
        eventBus.$on(MessageTypes.OPEN_COMPANY_LOCATION_DIALOG, (editedCompanyLocation: CompanyLocationV2Dto) => {
          state.serverErrors = new Map<string, string>();

          if (editedCompanyLocation) {
            state.edit = true;
            state.companyLocation = {...editedCompanyLocation};
            if (state.companyLocation.name) state.oldCompanyLocationName = state.companyLocation.name;
          } else {
            state.edit = false;
            state.companyLocation = {} as CompanyLocationV2Dto;
            state.oldCompanyLocationName = "";
          }
          state.dialog = true;
        });
      });

      const onCompanyLocationChanged = (newCompanyLocation: CompanyLocationV2Dto) => {
        state.companyLocation = newCompanyLocation;
      };

      // The v2 API answers a rejected company location with a problem detail instead of errors per field. A 409 (the
      // name is taken) is already shown by the global error handler, a 422 is not.
      const showProblem = (error: AxiosError, fallbackMessage: string) => {
        if (error && error.response && error.response.status === 422) {
          // @ts-ignore
          eventBus.$emit(MessageTypes.ERROR, error.response.data.detail || error.response.data.title);
        } else if (!error || !error.response || error.response.status !== 409) {
          eventBus.$emit(MessageTypes.ERROR, fallbackMessage);
        }
      };

      // Blank optional fields are sent as null, as the v2 API rejects an empty zip
      const toRequest = (companyLocation: CompanyLocationV2Dto): CompanyLocationV2Request => ({
        name: companyLocation.name.trim(),
        street: companyLocation.street?.trim() || null,
        zip: companyLocation.zip?.trim() || null,
        city: companyLocation.city?.trim() || null,
        notes: companyLocation.notes?.trim() || null
      });

      const save = () => {
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          const request = toRequest(state.companyLocation);

          if (!state.edit) {
            CompanyLocationService.createCompanyLocation(request)
              .then(newCompanyLocation => {
                eventBus.$emit(MessageTypes.COMPANY_LOCATION_CREATED, newCompanyLocation);
                closeDialog();
                eventBus.$emit(MessageTypes.SUCCESS, t("inventories.companyLocations.message.success.create"), 1000);
              })
              .catch((error: AxiosError) => showProblem(error, t("inventories.companyLocations.message.error.create")));
          } else {
            CompanyLocationService.updateCompanyLocation(state.companyLocation.id, request)
              .then(newCompanyLocation => {
                eventBus.$emit(MessageTypes.COMPANY_LOCATION_CREATED, newCompanyLocation);
                closeDialog();
              })
              .catch((error: AxiosError) => showProblem(error, t("inventories.companyLocations.message.error.edit")));
          }
        }
      };

      const closeDialog = () => {
        state.dialog = false;
        state.companyLocation = {} as CompanyLocationV2Dto;
        state.oldCompanyLocationName = "";
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
      };

      const rules = computed(() => ({
        companyLocation: {
          name: {
            required: helpers.withMessage(t("inventories.companyLocations.validation.name"), required),
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, state);

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          const model = v$.value.companyLocation;
          if (model.$dirty) {
            if(model.name.$error) {
              errors.set("name", model.name.$errors[0].$message);
            }
          }
        }
        return errors;
      });

      return {
        ...toRefs(state),
        onCompanyLocationChanged,
        save,
        closeDialog,
        errors
      };
    }
  });
